import { useQuery } from '@tanstack/react-query';
import { api } from '~/lib/api-client';
import { appSearchKeys } from '~/lib/query-keys';

/**
 * FRONT-2142 — the command palette's read across everything in the `apps`
 * table: apps, slide decks and sketches, in one request.
 *
 * Replaces the palette's two calls to `useDecksList` + `useSketchesList`
 * (FRONT-1974). That pairing was deliberate at the time — sharing the
 * galleries' cache keys let the two surfaces warm each other — but it did not
 * survive adding a third kind: it meant three requests per keystroke, each
 * pulling a gallery's full page size and full `DeckWire`/`SketchWire` shape
 * (Share audiences, audience locks, domain fields) to render a dropdown row.
 * One endpoint with one narrow row per hit is the better trade, and the
 * galleries keep their own hooks unchanged.
 */
export interface AppSearchHit {
  slug: string;
  kind: 'app' | 'deck' | 'sketch';
  title: string;
  description: string | null;
  /** Always `/apps/<slug>/` — the served page, for every kind. */
  path: string;
  workspaceShared: boolean;
  isOwner: boolean;
  /** FRONT-2937: one of the app's editors; their hit opens the Edit page. Optional so a cached payload reads as not. */
  isEditor?: boolean;
  /**
   * Owner-only on the wire. For a non-owner this is `null`, and `null` means
   * UNKNOWN — never "nobody". Rendering it as nobody is the wrong-audience bug
   * FRONT-1880 fixed and FRONT-1974's review panel caught a second time.
   */
  people: string[] | null;
  updatedAt: string;
}

export interface AppSearchResults {
  app: AppSearchHit[];
  deck: AppSearchHit[];
  sketch: AppSearchHit[];
}

const EMPTY: AppSearchResults = { app: [], deck: [], sketch: [] };

/**
 * `kinds` is the caller's feature-flag decision made explicit on the wire: a
 * workspace with Slides turned off does not ask for decks, so it cannot render
 * an empty Slides heading — nor one reading zero, which FRONT-1974 called out
 * as the same bug wearing a number.
 *
 * Normalized ONCE here and used for BOTH the cache key and the request param,
 * so a cache entry can never describe a different query than the one it holds
 * (FRONT-1501).
 */
export function useAppsSearch(opts?: {
  query?: string;
  kinds?: Array<'app' | 'deck' | 'sketch'>;
  enabled?: boolean;
}) {
  const q = opts?.query?.trim() || undefined;
  // Sorted so the same set in a different order is the same cache entry rather
  // than a second identical request under a second key.
  //
  // An ABSENT `kinds` omits the parameter, which the server reads as "every
  // kind". An explicitly EMPTY one sends an empty parameter, which it reads as
  // "no kinds" — not as "every kind" (review finding, 2026-09-18). The
  // distinction only matters if a caller ever flags every kind off at once,
  // but getting it backwards would show them exactly what they turned off.
  const kinds = opts?.kinds ? [...opts.kinds].sort().join(',') : undefined;

  const query = useQuery<AppSearchResults>({
    queryKey: appSearchKeys.list(q, kinds),
    queryFn: () => {
      const params = new URLSearchParams();
      if (q) params.set('q', q);
      if (kinds !== undefined) params.set('kinds', kinds);
      const qs = params.toString();
      return api.get<AppSearchResults>(`/api/apps-search${qs ? `?${qs}` : ''}`);
    },
    enabled: opts?.enabled,
  });

  return {
    // A settled-but-empty result and an in-flight one both read as empty here,
    // which is what the palette wants — it gates its own sections on the
    // loading flag, not on emptiness.
    results: query.data ?? EMPTY,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
  };
}
